import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { addDays } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  Position,
} from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { Card } from '@sorare/core/src/components/card/Card';
import { DumbLoanWrapper } from '@sorare/core/src/components/card/Loan';
import { LineupCardsLayout } from '@sorare/core/src/components/lineup/LineupCardsLayout';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { CARD_BORDER_RADIUS_2024 } from '@sorare/core/src/lib/cards';
import { glossary } from '@sorare/core/src/lib/glossary';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';
import { ClickInstantBuy_Flow } from '@sorare/core/src/protos/events/so5/shared/events';
import { PrimaryBuyBuyField } from '@sorare/marketplace/src/components/primaryBuy/PrimaryBuyBuyField';

import type { TeamPreview_loanableRoster } from './__generated__/index.graphql.ts';

const CardWrapper = styled.div`
  position: relative;
  &.highlighted {
    outline: 2px solid var(--c-blue-400);
    border-radius: ${CARD_BORDER_RADIUS_2024};

    &::after {
      position: absolute;
      content: '';
      inset: 0;
      border-radius: ${CARD_BORDER_RADIUS_2024};
      background: radial-gradient(
        154.46% 96.82% at 50% 3.18%,
        transparent 70%,
        var(--c-blue-400) 100%
      );
      z-index: 1;
    }
  }
`;

const ScoreWrapper = styled.div`
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translate(-50%, 50%);
  z-index: 2;
`;

const LineupCardsLayoutWrapper = styled.div`
  max-width: 500px;
  margin: 0 auto;
`;

const LineupButton = styled(ButtonBase)`
  display: flex;
  align-items: flex-start;
  isolation: isolate;
`;

const Absolute = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
`;

const Price = styled(Vertical)`
  position: relative;
  isolation: isolate;
  padding-bottom: var(--triple-unit);
  width: 100%;
`;

type LoanTeamCard =
  TeamPreview_loanableRoster['primaryOffers'][number]['anyCards'][number];

const Item = ({
  card,
  highlighted,
  showCheapestBadge,
  onClick,
}: {
  card: LoanTeamCard;
  highlighted: boolean;
  showCheapestBadge?: boolean;
  onClick: () => void;
}) => {
  const { main } = useAmountWithConversion({
    monetaryAmount: card.livePrimaryOffer?.price || MonetaryAmount.zero,
  });

  const endDate = addDays(new Date(), 21);

  return (
    <LineupButton onClick={onClick}>
      <Vertical gap={3} center>
        <DumbLoanWrapper
          gameWeekLeft={3}
          endDate={endDate}
          visible={!highlighted}
        >
          <CardWrapper className={classNames({ highlighted })}>
            <Card card={card} />
            <ScoreWrapper>
              <PlayerScore
                player={card.anyPlayer}
                mode={AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE}
              />
            </ScoreWrapper>
          </CardWrapper>
        </DumbLoanWrapper>
        <Price>
          {!!card.livePrimaryOffer?.price && <LabelM bold>{main}</LabelM>}
          {!!showCheapestBadge && (
            <Absolute>
              <Chip color="yellow">
                <FormattedMessage
                  id="LoanTeamSelection.lowestPrice"
                  defaultMessage="Lowest price"
                />
              </Chip>
            </Absolute>
          )}
        </Price>
      </Vertical>
    </LineupButton>
  );
};

const getCheapestCard = (cards: LoanTeamCard[]) => {
  return cards.reduce<LoanTeamCard | undefined>((acc, card) => {
    if (!acc) return card;
    if (!card.livePrimaryOffer?.price?.eurCents) return acc;
    if (
      card.livePrimaryOffer.price.eurCents <
      (acc.livePrimaryOffer?.price?.eurCents || 0)
    )
      return card;
    return acc;
  }, cards[0]);
};

const sortedPostions: Position[] = [
  Position.Goalkeeper,
  Position.Defender,
  Position.Midfielder,
  Position.Forward,
];

export const TeamPreview = ({
  roster,
  isSelected,
  hasLoanAssigned,
  onBuySuccess,
  onBuyConfirmationClose,
}: {
  roster: TeamPreview_loanableRoster;
  hasLoanAssigned: boolean;
  isSelected: boolean;
  onBuySuccess?: () => void;
  onBuyConfirmationClose?: () => void;
}) => {
  const cards = roster.primaryOffers
    .map(offer => offer.anyCards[0])
    .sort(
      (a, b) =>
        sortedPostions.indexOf(a.anyPositions[0]) -
        sortedPostions.indexOf(b.anyPositions[0])
    );

  const cheapestCard = getCheapestCard(cards);

  const [selectedCard, setSelectedCard] = useState<LoanTeamCard>(
    cheapestCard || cards[0]
  );

  return (
    <>
      <LineupCardsLayoutWrapper>
        <LineupCardsLayout fillHeightPercent={0.7} gap={0.5}>
          {cards.map(card => (
            <Item
              key={card.slug}
              highlighted={selectedCard.slug === card.slug && !hasLoanAssigned}
              showCheapestBadge={
                cheapestCard?.slug === card.slug && !hasLoanAssigned
              }
              card={card}
              onClick={() => setSelectedCard(card)}
            />
          ))}
        </LineupCardsLayout>
      </LineupCardsLayoutWrapper>
      {!!isSelected && !hasLoanAssigned && (
        <Portal id="loan-lineup-footer">
          <PrimaryBuyBuyField
            size="large"
            color="primary"
            card={selectedCard}
            cta={
              <FormattedMessage
                {...glossary.buyWithName}
                values={{ name: selectedCard.anyPlayer.displayName }}
              />
            }
            primaryBuyConfirmationOptions={{
              actions: {
                secondary: {
                  label: <FormattedMessage {...glossary.continue} />,
                },
              },
            }}
            onSuccess={onBuySuccess}
            onBuyConfirmationClose={onBuyConfirmationClose}
            onlyCreditCards
            fullWidth={false}
            origin={ClickInstantBuy_Flow.LOAN_FLOW}
            rosterId={roster.id}
          />
        </Portal>
      )}
    </>
  );
};

TeamPreview.fragments = {
  loanableRoster: gql`
    fragment TeamPreview_loanableRoster on LoanableRoster {
      id
      primaryOffers {
        id
        anyCards {
          slug
          anyPositions
          averageScore(type: LAST_FIFTEEN_SO5_AVERAGE_SCORE)
          anyPlayer {
            slug
            ...PlayerScore_anyPlayer
          }
          ...Card_anyCard
          ...PrimaryBuyBuyField_anyCard
        }
      }
    }
    ${Card.fragments.anyCard}
    ${PrimaryBuyBuyField.fragments.anyCard}
    ${PlayerScore.fragments.anyPlayer}
  ` as TypedDocumentNode<TeamPreview_loanableRoster>,
};
